import { Color, Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
import { useFocusVisible } from 'react-aria';
import styled, { css } from 'styled-components';

import Button from 'design-system/components/Button';
import { isActive, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { paragraphMixins } from 'design-system/mixins/text';

export interface BannerProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  variant?: 'basic' | 'success' | 'warning' | 'alert';
  action?: string | ReactElement;
  onClose?: VoidFunction;
  extendContentWidth?: boolean;
}

const StyledBanner = styled('div')<BannerProps>`
  ${paragraphMixins.sm};
  line-height: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px ${Spacing[2]};
  border-radius: 4px;
  background: ${({ variant }) => {
    switch (variant) {
      case 'success':
        return Color.CoreGreen100;
      case 'warning':
        return Color.CoreYellow100;
      case 'alert':
        return Color.CoreRed100;
      case 'basic':
      default:
        return Color.BackgroundSecondary;
    }
  }};
`;

const BannerContent = styled.div<{ extendContentWidth?: boolean }>`
  display: flex;
  align-items: center;

  > svg:first-child {
    margin-right: ${Spacing[2]};
  }

  ${({ extendContentWidth }) =>
    extendContentWidth &&
    css`
      width: 100%;
    `}
`;

const ActionButton = styled(Button)<{ isFocusVisible?: boolean }>`
  background-color: transparent;
  justify-self: space-between;

  &${isActive}${isNotDisabled} {
    background-color: unset;
  }
  &${isHovered}${isNotDisabled} {
    box-shadow: none;
  }

  ${({ isFocusVisible }) =>
    !isFocusVisible &&
    css`
      &${isHovered}${isFocused}${isNotDisabled} {
        box-shadow: none;
      }
      &${isFocused}${isNotDisabled} {
        box-shadow: none;
      }
    `}
`;

const Banner = ({
  children,
  variant,
  action,
  onClose,
  extendContentWidth,
  ...elemProps
}: BannerProps) => {
  const { isFocusVisible } = useFocusVisible();

  return (
    <StyledBanner variant={variant} role='alert' {...elemProps}>
      <BannerContent extendContentWidth={extendContentWidth}>{children}</BannerContent>
      {typeof action === 'string'
        ? onClose && (
            <ActionButton variant='basic' width='auto' size='sm' onClick={onClose}>
              {action}
            </ActionButton>
          )
        : action}
      {!action && onClose && (
        <ActionButton
          variant='basic'
          width='icon'
          aria-label='Close'
          title='Close'
          onClick={onClose}
          isFocusVisible={isFocusVisible}
        >
          <XIcon />
        </ActionButton>
      )}
    </StyledBanner>
  );
};

export default Banner;
